import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useLanguage } from './i18n';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { LogIn, Loader2, Mail, ArrowRight, ArrowLeft, CheckCircle, KeyRound, Plus, Globe } from 'lucide-react';
import axios from 'axios';
import ChurchRegistration from './ChurchRegistration';

// Backend URL - Use environment variable in production
const API = process.env.REACT_APP_BACKEND_URL 
  || (window.location.origin.includes('localhost')
    ? 'http://localhost:8001'
    : window.location.origin);

const LoginPage = () => {
  const { login } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Page mode: 'login', 'forgot', 'reset', 'register'
  const [pageMode, setPageMode] = useState('login');
  
  // Forgot Password States
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  
  // Reset Password States
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  // Arrow icon based on direction
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  // Check for reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    if (token) {
      setResetToken(token);
      setPageMode('reset');
      verifyResetToken(token);
    }
  }, []);

  const verifyResetToken = async (token) => {
    try {
      await axios.get(`${API}/api/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
    } catch (err) {
      setTokenValid(false);
      setResetError(err.response?.data?.detail || t('invalidLinkMessage'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Always remember the user (true by default)
    const result = await login(username, password, true);

    if (!result.success) {
      setError(result.message);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword !== confirmPassword) {
      setResetError(t('passwordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setResetError(t('passwordTooShort'));
      return;
    }

    setResetLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      setResetSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      setResetError(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setResetLoading(false);
    }
  };

  const backToLogin = () => {
    setPageMode('login');
    setForgotSuccess(false);
    setResetSuccess(false);
    setForgotError('');
    setResetError('');
    setForgotEmail('');
    setNewPassword('');
    setConfirmPassword('');
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Language Toggle Button Component
  const LanguageButton = () => (
    <button
      onClick={toggleLanguage}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full 
        bg-white/90 hover:bg-white border border-amber-200 shadow-sm
        transition-all duration-200 hover:shadow text-amber-700"
      title={t('language')}
    >
      <Globe className="w-4 h-4" />
      <span>{language === 'ar' ? 'Deutsch' : 'عربي'}</span>
    </button>
  );

  // Church Registration Page
  if (pageMode === 'register') {
    return (
      <ChurchRegistration 
        onBack={backToLogin}
        onSuccess={backToLogin}
      />
    );
  }

  // Reset Password Form
  if (pageMode === 'reset') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 p-4">
        <Card className="w-full max-w-md shadow-2xl border-amber-200">
          <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg py-6">
            <div className="flex justify-center mb-4">
              <img 
                src="/icon-192.png" 
                alt="Logo" 
                className="w-16 h-16 rounded-full shadow-lg border-4 border-white/30"
              />
            </div>
            <CardTitle className="text-center text-2xl font-bold">
              {t('resetPassword')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {resetSuccess ? (
              <div className="text-center space-y-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-800">{t('resetSuccess')}</h3>
                <p className="text-gray-600">{t('resetSuccessMessage')}</p>
                <Button
                  onClick={backToLogin}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                >
                  <LogIn className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('login')}
                </Button>
              </div>
            ) : tokenValid === false ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <KeyRound className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">{t('invalidLink')}</h3>
                <p className="text-gray-600">{resetError}</p>
                <Button onClick={backToLogin} variant="outline" className="w-full">
                  <BackArrow className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('backToLogin')}
                </Button>
              </div>
            ) : tokenValid === null ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
                <p className="mt-4 text-gray-600">{t('verifyingLink')}</p>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('newPassword')}</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('enterNewPassword')}
                    required
                    disabled={resetLoading}
                    className={isRTL ? 'text-right' : 'text-left'}
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('confirmPassword')}</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('reenterPassword')}
                    required
                    disabled={resetLoading}
                    className={isRTL ? 'text-right' : 'text-left'}
                    minLength={6}
                  />
                </div>
                {resetError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {resetError}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                >
                  {resetLoading ? (
                    <><Loader2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} animate-spin`} />{t('resetting')}</>
                  ) : (
                    <><KeyRound className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t('resetPassword')}</>
                  )}
                </Button>
                <Button type="button" onClick={backToLogin} variant="ghost" className="w-full text-gray-600">
                  <BackArrow className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('backToLogin')}
                </Button>
              </form>
            )}
            {/* Language Toggle */}
            <div className="mt-6 flex justify-center">
              <LanguageButton />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password Form
  if (pageMode === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 p-4">
        <Card className="w-full max-w-md shadow-2xl border-amber-200">
          <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg py-6">
            <div className="flex justify-center mb-4">
              <img 
                src="/icon-192.png" 
                alt="Logo" 
                className="w-16 h-16 rounded-full shadow-lg border-4 border-white/30"
              />
            </div>
            <CardTitle className="text-center text-2xl font-bold">{t('forgotPasswordTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {forgotSuccess ? (
              <div className="text-center space-y-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-800">{t('emailSent')}</h3>
                <p className="text-gray-600">{t('emailSentMessage')}</p>
                <p className="text-sm text-gray-500">{t('checkInbox')}</p>
                <Button onClick={backToLogin} variant="outline" className="w-full mt-4">
                  <BackArrow className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('backToLogin')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <p className="text-gray-600 text-sm text-center mb-4">
                  {t('forgotPasswordInstructions')}
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('email')}</label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t('enterEmail')}
                    required
                    disabled={forgotLoading}
                    className={isRTL ? 'text-right' : 'text-left'}
                    dir="ltr"
                  />
                </div>
                {forgotError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {forgotError}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                >
                  {forgotLoading ? (
                    <><Loader2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} animate-spin`} />{t('sending')}</>
                  ) : (
                    <><Mail className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t('sendResetLink')}</>
                  )}
                </Button>
                <Button type="button" onClick={backToLogin} variant="ghost" className="w-full text-gray-600">
                  <BackArrow className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('backToLogin')}
                </Button>
              </form>
            )}
            {/* Language Toggle */}
            <div className="mt-6 flex justify-center">
              <LanguageButton />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 p-4">
      <Card className="w-full max-w-md shadow-2xl border-amber-200">
        <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg py-6">
          {/* Two Logos Side by Side */}
          <div className="flex justify-center items-center gap-4 mb-4">
            <img 
              src="/diocese-logo.png" 
              alt="Diocese Logo" 
              className="w-20 h-20 rounded-full shadow-lg border-4 border-white/30 bg-white"
            />
            <img 
              src="/icon-192.png" 
              alt="Church Logo" 
              className="w-20 h-20 rounded-full shadow-lg border-4 border-white/30"
            />
          </div>
          <p className="text-center text-amber-100 text-sm">{t('patriarchate')}</p>
          <p className="text-center text-amber-200 text-xs mb-2">{t('diocese')}</p>
          <CardTitle className="text-center text-2xl font-bold">
            {t('appName')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('username')}</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('enterUsername')}
                required
                disabled={loading}
                className={isRTL ? 'text-right' : 'text-left'}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('password')}</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('enterPassword')}
                required
                disabled={loading}
                className={isRTL ? 'text-right' : 'text-left'}
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  className={`w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 ${isRTL ? 'ml-2' : 'mr-2'}`}
                />
                <label htmlFor="rememberMe" className="text-sm text-gray-700">{t('rememberMe')}</label>
              </div>
              <button
                type="button"
                onClick={() => setPageMode('forgot')}
                className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
              >
                {t('forgotPassword')}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold py-3"
            >
              {loading ? (
                <><Loader2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} animate-spin`} />{t('loggingIn')}</>
              ) : (
                <><LogIn className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t('login')}</>
              )}
            </Button>
          </form>

          {/* Language Toggle - Below Login Button */}
          <div className="mt-4 flex justify-center">
            <LanguageButton />
          </div>

          {/* Register New Church */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500 mb-3">{t('newChurch')}</p>
            <Button
              onClick={() => setPageMode('register')}
              variant="outline"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Plus className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('registerNewChurch')}
            </Button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>{t('deaconManagement')}</p>
            <p className="mt-1">{t('copyright')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
